import { formatUnitPrice } from "./consts";
import {
  PricingKeyOption,
  PricingPack,
  PricingValueKind,
  PricingVersion,
} from "./types";

/**
 * Vrijednost s jedinicom mjere.
 *
 * Bez ovoga bi probni period od 14 dana pisao „14,00 €", jer razlaganje s poslužitelja vraća
 * gole brojeve, a jedinicu zna samo cjenik.
 */
export const formatPricingValue = (value: number, kind: PricingValueKind): string => {
  switch (kind) {
    case "Money":
      return formatUnitPrice.format(value);
    case "Percent":
      return `${value.toFixed(2).replace(".", ",")} %`;
    case "Days":
      return `${value.toFixed(0)} dana`;
    default:
      return value.toFixed(0);
  }
};

/** Jedinica koja stoji u polju za unos. Novac i postotak nose znak, ostalo riječ. */
export const pricingValueUnit = (kind: PricingValueKind): string => {
  switch (kind) {
    case "Money":
      return "€";
    case "Percent":
      return "%";
    case "Days":
      return "dana";
    default:
      return "kom";
  }
};

/**
 * Samo novac trpi postotak, maržu i prag odobrenja.
 *
 * Kod dana i komada postotak nema smisla („−25 % probnog perioda"), a i smjer je obrnut: kod
 * cijene je minus pogodnost za klijenta, kod ograničenja je plus.
 */
export const isMoneyKey = (option: PricingKeyOption | undefined): boolean =>
  option?.valueKind === "Money";

/**
 * Ključevi cjenika složeni za odabir karticama.
 *
 * Šifra `vozilo.core+najam+prodaja` ne govori ništa dok se ne rastavi na nazive packova, pa se
 * ovdje spaja s cjenikom: naziv, kratki opis i vrijednost koja danas vrijedi. Nabavne cijene se
 * ne pojavljuju — one nisu ni u jednoj od tri liste koje verzija vraća.
 */
export const buildPricingKeyOptions = (
  version: PricingVersion,
  packs: PricingPack[]
): PricingKeyOption[] => {
  const packName = (code: string): string =>
    packs.find((pack) => pack.code === code)?.name ?? code;

  const combinations: PricingKeyOption[] = version.combinations.map((combination) => ({
    code: combination.keyCode,
    name: combination.packCodes.map(packName).join(" + "),
    description: combination.description,
    value: `${formatUnitPrice.format(combination.price)} / vozilo / mj`,
    valueKind: "Money",
    rawValue: combination.price,
    group: "Subscription",
  }));

  const metered: PricingKeyOption[] = version.meteredItems.map((item) => ({
    code: item.code,
    name: item.name,
    description: null,
    value: `${formatUnitPrice.format(item.price)} po komadu`,
    valueKind: "Money",
    rawValue: item.price,
    group: "Metered",
  }));

  const other: PricingKeyOption[] = version.otherValues.map((item) => ({
    code: item.code,
    name: item.name,
    description: null,
    value: formatPricingValue(item.value, item.valueKind),
    valueKind: item.valueKind,
    rawValue: item.value,
    group: "Other",
  }));

  return [...combinations, ...metered, ...other];
};
